/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Typography, Typography2 } from '@coursera/cds-core';

import { ShowMoreGridSection } from 'bundles/cds-labs/components/';
import type { ExpandButtonProps } from 'bundles/cds-labs/components/';
import CarouselCard from 'bundles/degree-description/components/modules/upsells/UpsellCard';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { UpsellsByDegreeSlug } from 'bundles/degree-description/types';
import {
  filterNullOrUndefined,
  getProductVariantString,
  getUpsellInfo,
  humanizeDifficultyLevel,
} from 'bundles/degree-description/utils/utils';
import TrackedButton from 'bundles/page/components/TrackedButton';

const styles = {
  background: () => css`
    padding: var(--cds-spacing-600) 0;
  `,
  gridContainer: () => css`
    padding: 0;
    margin-top: calc(var(--cds-spacing-150) * -1);
  `,
};

type UpsellItem = {
  id: string;
  card: React.ReactElement;
};

type Props = {
  title: string;
  description: string;
  disclaimer?: string;
  upsells: NonNullable<UpsellsByDegreeSlug>;
  trackingNamePrefix: string;
  trackingIndex?: number;
};

export const UpsellsModule = (props: Props): React.ReactElement => {
  const { title, description, disclaimer, upsells, trackingNamePrefix, trackingIndex } = props;

  const { trackClickButton, useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('upsell', trackingIndex);

  const renderExpandButton = ({ onClick, defaultLabel, onKeyDown, isExpanded }: ExpandButtonProps) => {
    const handleExpandButtonClick = () => {
      if (isExpanded) {
        trackClickButton('show_more_product_cards', 'upsell');
      }

      return onClick;
    };

    return (
      <Button
        size="small"
        variant="secondary"
        onKeyDown={onKeyDown}
        onClick={handleExpandButtonClick()}
        component={TrackedButton}
        trackingName={`${trackingNamePrefix}_upsell_show_all`}
        withVisibilityTracking={false}
        requireFullyVisible={false}
      >
        {defaultLabel}
      </Button>
    );
  };

  const upsellItems: UpsellItem[] = upsells
    .map((upsell, index) => {
      const upsellInfo = getUpsellInfo([upsell]);
      if (!upsellInfo) {
        return null;
      }

      const difficultyLevel = humanizeDifficultyLevel(upsellInfo.level);
      const productVariantString = getProductVariantString(upsellInfo.upsell?.productVariant);

      return {
        id: upsellInfo.upsell?.id,
        card: (
          <div
            css={css`
              padding: 12px 0;
              height: 100%;
            `}
          >
            <CarouselCard
              key={upsellInfo.upsell.id}
              title={upsellInfo.upsell.name || ''}
              bannerImageUrl={upsellInfo.upsellHeaderImage}
              slug={upsellInfo.upsell.slug}
              productVariant={upsellInfo.upsell.productVariant || ''}
              partners={upsellInfo.upsellPartners}
              tagline={upsellInfo.tagline}
              trackingNamePrefix={trackingNamePrefix}
              footerText={[difficultyLevel, productVariantString]}
              loading={!upsellInfo.upsell}
              productId={upsellInfo.upsell.id}
              index={index}
              productUrl={upsellInfo.url}
              isFixedHeight={false}
            />
          </div>
        ),
      };
    })
    .filter(filterNullOrUndefined);

  const theme = useDegreeDescriptionPageTheme();
  return (
    <div css={styles.background} ref={pageSectionRef}>
      <Container>
        <Grid container direction="column" spacing={24}>
          <Grid item container direction="column" spacing={24} md={8}>
            <Grid item>
              <Typography variant="h1semibold" component="h2" css={theme.mobileFontSizeStyle?.header}>
                {title}
              </Typography>
            </Grid>

            <Grid item>
              <Typography2 component="p" css={theme.mobileFontSizeStyle?.body}>
                {description}
              </Typography2>
            </Grid>
          </Grid>

          <Grid item>
            <ShowMoreGridSection<UpsellItem>
              css={styles.gridContainer}
              items={upsellItems}
              itemsGridConfig={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              renderItem={({ item }) => item.card}
              renderExpandButton={renderExpandButton}
              spacing={24}
              title=""
            />
          </Grid>

          {disclaimer && (
            <Grid item>
              <Typography2 component="p" css={theme.mobileFontSizeStyle?.body}>
                {disclaimer}
              </Typography2>
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default UpsellsModule;
